import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Calendar } from 'lucide-react'
import clsx from 'clsx'
import { Modal } from './Modal'
import { Button } from './Button'

interface Props {
  /** ISO date string `YYYY-MM-DD` or empty string. */
  value: string
  onChange: (iso: string) => void
  minAge?: number
  maxAge?: number
  className?: string
}

const ITEM_HEIGHT = 36
const VISIBLE_ROWS = 5 // selected centre + 2 above + 2 below
const PADDING_ROWS = Math.floor(VISIBLE_ROWS / 2)

/**
 * iOS-style 3-wheel date picker (day / month / year). Each wheel is a
 * vertical scroll container with `scroll-snap-type: y mandatory`, items
 * sized exactly `ITEM_HEIGHT`. The selected value is the item closest
 * to the centre after scrolling settles.
 *
 * Native momentum scroll on mobile makes this feel right; on desktop
 * the user can scroll-wheel or drag.
 */
export function DatePicker({ value, onChange, minAge = 0, maxAge = 100, className }: Props) {
  const { i18n } = useTranslation()
  const lang = (i18n.language === 'en' ? 'en' : 'ru') as 'ru' | 'en'

  const [y, m, d] = parseIso(value)

  const thisYear = new Date().getFullYear()
  const minYear = thisYear - maxAge
  const maxYear = thisYear - minAge

  // Year options (descending — newest first → selected default = recent)
  const years = useMemo(() => {
    const out: number[] = []
    for (let yr = maxYear; yr >= minYear; yr--) out.push(yr)
    return out
  }, [minYear, maxYear])

  const months = lang === 'en' ? MONTHS_EN : MONTHS_RU

  const daysInMonth = useMemo(() => {
    if (!y || !m) return 31
    return new Date(y, m, 0).getDate()
  }, [y, m])

  const days = useMemo(
    () => Array.from({ length: daysInMonth }, (_, i) => i + 1),
    [daysInMonth],
  )

  function emit(year: number, month: number, day: number) {
    const clampedDay = Math.min(day, new Date(year, month, 0).getDate())
    const iso = `${year}-${String(month).padStart(2, '0')}-${String(clampedDay).padStart(2, '0')}`
    onChange(iso)
  }

  // Default to a reasonable date (today minus minAge years) on first mount
  // when the caller has no value yet — gives the user something to scroll
  // FROM, not an empty state where every wheel is at index 0.
  useEffect(() => {
    if (!value) {
      const defaultYear = thisYear - Math.max(minAge, 20)
      emit(defaultYear, 1, 1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div
      className={clsx(
        'relative overflow-hidden rounded-2xl border border-sage-200 bg-white/80',
        className,
      )}
      style={{ height: ITEM_HEIGHT * VISIBLE_ROWS }}
    >
      {/* Centre highlight band — the selected row */}
      <div
        className="pointer-events-none absolute inset-x-0 z-10 border-y border-sage-300/70 bg-sage-100/40"
        style={{
          top: PADDING_ROWS * ITEM_HEIGHT,
          height: ITEM_HEIGHT,
        }}
      />
      {/* Top & bottom fade overlays */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-20 bg-gradient-to-b from-white/90 to-white/0"
        style={{ height: PADDING_ROWS * ITEM_HEIGHT }}
      />
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-white/90 to-white/0"
        style={{ height: PADDING_ROWS * ITEM_HEIGHT }}
      />

      <div className="relative z-0 grid h-full grid-cols-3">
        <Wheel
          items={days.map((dd) => ({ value: dd, label: String(dd) }))}
          value={d ?? days[0]!}
          onChange={(newD) => emit(y ?? thisYear - minAge, m ?? 1, newD)}
        />
        <Wheel
          items={months.map((label, i) => ({ value: i + 1, label }))}
          value={m ?? 1}
          onChange={(newM) => emit(y ?? thisYear - minAge, newM, d ?? 1)}
        />
        <Wheel
          items={years.map((yr) => ({ value: yr, label: String(yr) }))}
          value={y ?? years[0]!}
          onChange={(newY) => emit(newY, m ?? 1, d ?? 1)}
        />
      </div>
    </div>
  )
}

interface WheelItem {
  value: number
  label: string
}

function Wheel({
  items,
  value,
  onChange,
}: {
  items: WheelItem[]
  value: number
  onChange: (v: number) => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  const settling = useRef<number | null>(null)
  const dragState = useRef<{ startY: number; startScroll: number; pointerId: number } | null>(null)
  const [centerIndex, setCenterIndex] = useState(
    Math.max(0, items.findIndex((it) => it.value === value)),
  )

  // Sync external value → scroll position. Skip when the user is mid-scroll.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const idx = items.findIndex((it) => it.value === value)
    if (idx < 0) return
    setCenterIndex(idx)
    requestAnimationFrame(() => {
      el.scrollTo({ top: idx * ITEM_HEIGHT, behavior: 'auto' })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, items.length])

  function handleScroll() {
    const el = ref.current
    if (!el) return
    const raw = el.scrollTop / ITEM_HEIGHT
    const idx = Math.max(0, Math.min(items.length - 1, Math.round(raw)))
    if (idx !== centerIndex) setCenterIndex(idx)

    if (settling.current) clearTimeout(settling.current)
    settling.current = window.setTimeout(() => {
      const settled = items[idx]
      if (settled && settled.value !== value) onChange(settled.value)
    }, 120)
  }

  // Mouse drag — touch already works through native overflow scroll, but
  // mouse can't grab a hidden scrollbar. We listen to pointer events and
  // bypass for `touch` pointerType (let the browser handle it natively).
  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (e.pointerType === 'touch') return
    const el = ref.current
    if (!el) return
    dragState.current = { startY: e.clientY, startScroll: el.scrollTop, pointerId: e.pointerId }
    el.setPointerCapture(e.pointerId)
    el.style.scrollSnapType = 'none' // suspend snap while dragging — re-enabled on release
  }
  function handlePointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!dragState.current || e.pointerId !== dragState.current.pointerId) return
    const el = ref.current
    if (!el) return
    const dy = e.clientY - dragState.current.startY
    el.scrollTop = dragState.current.startScroll - dy
  }
  function handlePointerUp(e: React.PointerEvent<HTMLDivElement>) {
    if (!dragState.current || e.pointerId !== dragState.current.pointerId) return
    const el = ref.current
    if (el) {
      el.releasePointerCapture(e.pointerId)
      // Re-enable snap on the next frame and nudge by 1px to trigger it.
      requestAnimationFrame(() => {
        el.style.scrollSnapType = 'y mandatory'
        const idx = Math.max(0, Math.min(items.length - 1, Math.round(el.scrollTop / ITEM_HEIGHT)))
        el.scrollTo({ top: idx * ITEM_HEIGHT, behavior: 'smooth' })
      })
    }
    dragState.current = null
  }

  return (
    <div
      ref={ref}
      onScroll={handleScroll}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      className="snap-y snap-mandatory overflow-y-scroll scrollbar-none"
      style={{
        scrollbarWidth: 'none',
        // Disable text selection during drag so mouse drag feels native.
        userSelect: 'none',
        touchAction: 'pan-y',
      }}
    >
      <div style={{ height: PADDING_ROWS * ITEM_HEIGHT }} />
      {items.map((item, idx) => {
        const dist = Math.abs(idx - centerIndex)
        const opacity = dist === 0 ? 1 : dist === 1 ? 0.55 : 0.3
        return (
          <div
            key={item.value}
            className="snap-center flex items-center justify-center text-base text-sage-800"
            style={{ height: ITEM_HEIGHT, opacity, fontWeight: dist === 0 ? 500 : 400 }}
          >
            {item.label}
          </div>
        )
      })}
      <div style={{ height: PADDING_ROWS * ITEM_HEIGHT }} />
    </div>
  )
}

/**
 * Trigger button + Modal containing the wheel DatePicker. Replaces inline
 * `<input type="date">` everywhere — gives a consistent, finger-scrollable
 * wheel picker across desktop and mobile.
 *
 * The trigger renders as a styled input-pill showing the formatted date
 * (or a placeholder when empty). Tap opens a modal with the wheels +
 * Cancel/OK; OK commits the staged date back through `onChange`.
 */
interface DateFieldProps {
  value: string
  onChange: (iso: string) => void
  placeholder?: string
  minAge?: number
  maxAge?: number
  /** Optional class applied to the trigger button. */
  className?: string
  /** Modal title — defaults to t('date_picker.title'). */
  title?: string
}

export function DateField({
  value,
  onChange,
  placeholder,
  minAge,
  maxAge,
  className,
  title,
}: DateFieldProps) {
  const { t, i18n } = useTranslation()
  const lang = (i18n.language === 'en' ? 'en' : 'ru') as 'ru' | 'en'
  const [open, setOpen] = useState(false)
  const [staged, setStaged] = useState(value)

  // Keep the staged buffer in sync if the parent value changes while the
  // modal is closed (e.g. server-loaded user data arrives after first render).
  useEffect(() => {
    if (!open) setStaged(value)
  }, [value, open])

  const display = value ? formatHuman(value, lang) : (placeholder ?? t('date_picker.placeholder'))

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setStaged(value)
          setOpen(true)
        }}
        className={clsx(
          'flex w-full items-center gap-2 rounded-xl border border-sage-200 bg-white px-3 py-2 text-left text-sage-800 outline-none transition focus:border-sage-500 hover:bg-sage-50',
          !value && 'text-sage-400',
          className,
        )}
      >
        <Calendar className="h-4 w-4 shrink-0 text-sage-500" strokeWidth={1.8} aria-hidden />
        <span className="min-w-0 flex-1 truncate">{display}</span>
      </button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={title ?? t('date_picker.title')}
      >
        <div className="flex flex-col gap-4">
          <DatePicker
            value={staged}
            onChange={setStaged}
            minAge={minAge}
            maxAge={maxAge}
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="md" onClick={() => setOpen(false)}>
              {t('actions.cancel')}
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={() => {
                if (staged) onChange(staged)
                setOpen(false)
              }}
              disabled={!staged}
            >
              {t('actions.save')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}

function formatHuman(iso: string, lang: 'ru' | 'en'): string {
  const [y, m, d] = parseIso(iso)
  if (!y || !m || !d) return iso
  return new Intl.DateTimeFormat(lang === 'en' ? 'en-US' : 'ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(y, m - 1, d))
}

const MONTHS_RU = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
]
const MONTHS_EN = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

// =========================================================================
// Time picker — same wheel mechanic, two columns (hours / minutes)
// =========================================================================

interface TimePickerProps {
  /** `HH:MM` or empty string. */
  value: string
  onChange: (hhmm: string) => void
  /** Minute granularity (e.g. 5 means options 0, 5, 10, …). Default 5. */
  minuteStep?: number
  className?: string
}

export function TimePicker({ value, onChange, minuteStep = 5, className }: TimePickerProps) {
  const [hh, mm] = parseTime(value)
  const hours = useMemo(() => Array.from({ length: 24 }, (_, i) => i), [])
  const minutes = useMemo(() => {
    const out: number[] = []
    for (let m = 0; m < 60; m += minuteStep) out.push(m)
    return out
  }, [minuteStep])

  useEffect(() => {
    if (!value) emitTime(0, 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function emitTime(h: number, m: number) {
    onChange(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`)
  }

  // Snap the externally provided minute up to the nearest step option so
  // the wheel can land on a valid index even when source data has 1-min
  // precision (e.g. legacy birth times).
  const snappedMinute = mm == null ? 0 : minutes.reduce(
    (best, candidate) => Math.abs(candidate - mm) < Math.abs(best - mm) ? candidate : best,
    minutes[0]!,
  )

  return (
    <div
      className={clsx(
        'relative overflow-hidden rounded-2xl border border-sage-200 bg-white/80',
        className,
      )}
      style={{ height: ITEM_HEIGHT * VISIBLE_ROWS }}
    >
      <div
        className="pointer-events-none absolute inset-x-0 z-10 border-y border-sage-300/70 bg-sage-100/40"
        style={{ top: PADDING_ROWS * ITEM_HEIGHT, height: ITEM_HEIGHT }}
      />
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-20 bg-gradient-to-b from-white/90 to-white/0"
        style={{ height: PADDING_ROWS * ITEM_HEIGHT }}
      />
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-white/90 to-white/0"
        style={{ height: PADDING_ROWS * ITEM_HEIGHT }}
      />

      <div className="relative z-0 grid h-full grid-cols-2">
        <Wheel
          items={hours.map((h) => ({ value: h, label: String(h).padStart(2, '0') }))}
          value={hh ?? 0}
          onChange={(newH) => emitTime(newH, snappedMinute)}
        />
        <Wheel
          items={minutes.map((m) => ({ value: m, label: String(m).padStart(2, '0') }))}
          value={snappedMinute}
          onChange={(newM) => emitTime(hh ?? 0, newM)}
        />
      </div>
    </div>
  )
}

interface TimeFieldProps {
  value: string
  onChange: (hhmm: string) => void
  placeholder?: string
  disabled?: boolean
  minuteStep?: number
  className?: string
  title?: string
}

export function TimeField({
  value,
  onChange,
  placeholder,
  disabled,
  minuteStep,
  className,
  title,
}: TimeFieldProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [staged, setStaged] = useState(value)

  useEffect(() => {
    if (!open) setStaged(value)
  }, [value, open])

  const display = value || (placeholder ?? t('time_picker.placeholder'))

  return (
    <>
      <button
        type="button"
        onClick={() => {
          if (disabled) return
          setStaged(value || '12:00')
          setOpen(true)
        }}
        disabled={disabled}
        className={clsx(
          'flex w-full items-center gap-2 rounded-xl border border-sage-200 bg-white px-3 py-2 text-left text-sage-800 outline-none transition focus:border-sage-500 hover:bg-sage-50 disabled:bg-sage-50 disabled:text-sage-400',
          !value && 'text-sage-400',
          className,
        )}
      >
        <svg className="h-4 w-4 shrink-0 text-sage-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7 V12 L15 14" />
        </svg>
        <span className="min-w-0 flex-1 truncate">{display}</span>
      </button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={title ?? t('time_picker.title')}
      >
        <div className="flex flex-col gap-4">
          <TimePicker value={staged} onChange={setStaged} minuteStep={minuteStep} />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="md" onClick={() => setOpen(false)}>
              {t('actions.cancel')}
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={() => {
                if (staged) onChange(staged)
                setOpen(false)
              }}
              disabled={!staged}
            >
              {t('actions.save')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}

function parseTime(s: string): [number | null, number | null] {
  if (!s) return [null, null]
  const m = /^(\d{1,2}):(\d{1,2})$/.exec(s)
  if (!m) return [null, null]
  const hh = parseInt(m[1]!, 10)
  const mm = parseInt(m[2]!, 10)
  if (Number.isNaN(hh) || Number.isNaN(mm)) return [null, null]
  return [hh, mm]
}

function parseIso(iso: string): [number | null, number | null, number | null] {
  if (!iso) return [null, null, null]
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!m) return [null, null, null]
  return [parseInt(m[1]!, 10), parseInt(m[2]!, 10), parseInt(m[3]!, 10)]
}
